import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TabsContent } from '@/components/ui/tabs';
import { TrendingUp, ChevronDown } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

export const ValuationTab = ({ llc, balanceSheet, ownerships, user }) => {
  const [capRate, setCapRate] = useState(6.0);
  const [ratePerSqft, setRatePerSqft] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Load saved values from localStorage on mount
  useEffect(() => {
    if (llc?.id) {
      const savedCapRate = localStorage.getItem(`valuation_capRate_${llc.id}`);
      const savedRatePerSqft = localStorage.getItem(`valuation_ratePerSqft_${llc.id}`);
      
      if (savedCapRate !== null) {
        setCapRate(parseFloat(savedCapRate));
      }
      if (savedRatePerSqft !== null) {
        setRatePerSqft(parseFloat(savedRatePerSqft));
      } else if (llc?.lease_rate_per_sqft) {
        // Only use default if no saved value exists
        setRatePerSqft(llc.lease_rate_per_sqft);
      }
    }
  }, [llc?.id, llc?.lease_rate_per_sqft]);

  // Save values to localStorage when they change
  const handleCapRateChange = (value) => {
    const newValue = parseFloat(value) || 6;
    setCapRate(newValue);
    if (llc?.id) {
      localStorage.setItem(`valuation_capRate_${llc.id}`, newValue.toString());
    }
  };

  const handleRatePerSqftChange = (value) => {
    const newValue = parseFloat(value) || 0;
    setRatePerSqft(newValue);
    if (llc?.id) {
      localStorage.setItem(`valuation_ratePerSqft_${llc.id}`, newValue.toString());
    }
  };

  // Reset to original values and clear localStorage
  const handleReset = () => {
    const defaultRate = llc?.lease_rate_per_sqft || 0;
    setRatePerSqft(defaultRate);
    setCapRate(6.0);
    if (llc?.id) {
      localStorage.removeItem(`valuation_capRate_${llc.id}`);
      localStorage.removeItem(`valuation_ratePerSqft_${llc.id}`);
    }
  };

  if (!llc) return null;

  // Check if LLC info is verified (gold star)
  const isVerified = llc.info_current;

  return (
    <TabsContent value="valuation" className="mt-4">
      <Card 
        className="border-slate-200"
        style={isVerified ? {
          boxShadow: '0 0 0 2px rgba(251, 191, 36, 0.5), 0 4px 12px rgba(0, 0, 0, 0.1)',
          backgroundColor: 'rgba(254, 243, 199, 0.4)'
        } : {}}
      >
        <CardHeader 
          className="cursor-pointer hover:bg-slate-100 transition-colors py-3"
          style={isVerified ? {
            background: 'linear-gradient(to right, rgba(254, 243, 199, 0.9), rgba(254, 249, 195, 0.6))'
          } : {
            background: 'linear-gradient(to right, rgb(241, 245, 249), rgb(248, 250, 252))'
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="font-serif flex items-center gap-2 text-base">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isVerified ? 'bg-amber-200' : 'bg-emerald-100'}`}>
                  <TrendingUp className={`w-4 h-4 ${isVerified ? 'text-amber-700' : 'text-emerald-600'}`} />
                </div>
                Property Valuation
              </CardTitle>
              {isVerified ? (
                <span className="text-xs text-slate-600 font-medium bg-slate-200/60 px-2 py-0.5 rounded italic">Estimated</span>
              ) : (
                <span className="text-xs text-amber-600 italic">— Guesstimate based on Assumptions (could be higher or lower)</span>
              )}
            </div>
            <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </CardHeader>
        {isOpen && (
        <CardContent 
          className="pt-4 pb-4"
          style={isVerified ? { backgroundColor: 'rgba(254, 243, 199, 0.25)' } : {}}
        >
          {llc.llc_type === 'real_estate' ? (
            <div className="space-y-3">
              {/* Annual Rents Calculation */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2.5 border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium uppercase tracking-wide">Annual Rents</p>
                  <p className="text-base font-mono font-bold text-blue-800">
                    {llc.lease_sqft && ratePerSqft 
                      ? formatCurrency(llc.lease_sqft * ratePerSqft)
                      : '$0.00'
                    }
                  </p>
                  <p className="text-xs text-blue-600">
                    {llc.lease_sqft && ratePerSqft 
                      ? `${llc.lease_sqft.toLocaleString()} sf × $${ratePerSqft.toFixed(2)}/sf`
                      : 'Set lease info'
                    }
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-2.5 border border-amber-200">
                  <p className="text-xs text-amber-700 font-medium uppercase tracking-wide mb-2">Assumptions</p>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-amber-700 font-medium">$/SF:</span>
                      <Input
                        type="number"
                        step="0.25"
                        min="0"
                        value={ratePerSqft}
                        onChange={(e) => handleRatePerSqftChange(e.target.value)}
                        className="w-20 text-sm font-mono font-bold text-amber-800 border-amber-300 h-7 px-2"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-amber-700 font-medium">Cap Rate:</span>
                      <Input
                        type="number"
                        step="0.25"
                        min="1"
                        max="20"
                        value={capRate}
                        onChange={(e) => handleCapRateChange(e.target.value)}
                        className="w-16 text-sm font-mono font-bold text-amber-800 border-amber-300 h-7 px-2"
                      />
                      <span className="text-sm font-bold text-amber-800">%</span>
                    </div>
                    <button
                      onClick={handleReset}
                      className="text-xs text-amber-600 hover:text-amber-800 underline whitespace-nowrap"
                    >
                      Reset
                    </button>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-2.5 border border-slate-200">
                  <p className="text-xs text-slate-700 font-medium uppercase tracking-wide">Gross Value</p>
                  <p className="text-base font-mono font-bold text-slate-800">
                    {llc.lease_sqft && ratePerSqft && capRate > 0
                      ? formatCurrency((llc.lease_sqft * ratePerSqft) / (capRate / 100))
                      : '$0.00'
                    }
                  </p>
                  <p className="text-xs text-slate-600">Rents ÷ Cap Rate</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-2.5 border border-purple-200">
                  <p className="text-xs text-purple-700 font-medium uppercase tracking-wide">Appraised Value</p>
                  <p className="text-base font-mono font-bold text-purple-800">
                    {llc.appraised_value ? formatCurrency(llc.appraised_value) : '—'}
                  </p>
                  <p className="text-xs text-purple-600">Set in LLC Edit</p>
                </div>
              </div>

              {/* Liabilities and Net Value */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-2.5 border border-teal-200">
                  <p className="text-xs text-teal-700 font-medium uppercase tracking-wide">Cash/Checking</p>
                  <p className="text-base font-mono font-bold text-teal-800">
                    {formatCurrency(balanceSheet?.current_assets?.Checking || 0)}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-2.5 border border-rose-200">
                  <p className="text-xs text-rose-700 font-medium uppercase tracking-wide">Liabilities</p>
                  <p className="text-base font-mono font-bold text-rose-800">
                    {formatCurrency(balanceSheet?.total_liabilities || 0)}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-2.5 border border-emerald-200">
                  <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Net Value (Equity)</p>
                  <p className="text-lg font-mono font-bold text-emerald-800">
                    {llc.lease_sqft && ratePerSqft && capRate > 0
                      ? formatCurrency(((llc.lease_sqft * ratePerSqft) / (capRate / 100)) + (balanceSheet?.current_assets?.Checking || 0) - (balanceSheet?.total_liabilities || 0))
                      : '$0.00'
                    }
                  </p>
                  <p className="text-xs text-emerald-600">Gross + Cash − Liabilities</p>
                </div>
                
                {/* My Value - based on user's ownership */}
                {(() => {
                  const userOwnership = ownerships.find(o => o.investor_id === user?.investor_id);
                  if (!userOwnership || !llc.lease_sqft || !ratePerSqft || capRate <= 0) return null;
                  const grossValue = (llc.lease_sqft * ratePerSqft) / (capRate / 100);
                  const cashBalance = balanceSheet?.current_assets?.Checking || 0;
                  const totalLiabilities = balanceSheet?.total_liabilities || 0;
                  const netValue = grossValue + cashBalance - totalLiabilities;
                  const myValue = netValue * (userOwnership.percentage / 100);
                  return (
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-2.5 border border-indigo-200">
                      <p className="text-xs text-indigo-700 font-medium uppercase tracking-wide">My Value ({userOwnership.percentage.toFixed(1)}%)</p>
                      <p className="text-lg font-mono font-bold text-indigo-800">
                        {formatCurrency(myValue)}
                      </p>
                      <p className="text-xs text-indigo-600">{userOwnership.investor_name}</p>
                    </div>
                  );
                })()}
              </div>
              
              {(!llc.lease_sqft || !ratePerSqft) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <p className="text-amber-700">
                    <strong>Missing lease information.</strong> Please update the Tenant Info panel with lease square footage and rate per sq ft to calculate valuation.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Liquidating Cash Balance */
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-2.5 border border-amber-200">
                  <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">Cash Balance Value</p>
                  <p className="text-lg font-mono font-bold text-amber-800">
                    {formatCurrency(balanceSheet?.current_assets?.Checking || 0)}
                  </p>
                  <p className="text-xs text-amber-600">Current checking balance</p>
                </div>

                {/* My Value for Cash Balance */}
                {(() => {
                  const userOwnership = ownerships.find(o => o.investor_id === user?.investor_id);
                  if (!userOwnership) return null;
                  const cashBalance = balanceSheet?.current_assets?.Checking || 0;
                  const myValue = cashBalance * (userOwnership.percentage / 100);
                  return (
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-2.5 border border-indigo-200">
                      <p className="text-xs text-indigo-700 font-medium uppercase tracking-wide">My Value ({userOwnership.percentage.toFixed(1)}%)</p>
                      <p className="text-lg font-mono font-bold text-indigo-800">
                        {formatCurrency(myValue)}
                      </p>
                      <p className="text-xs text-indigo-600">{userOwnership.investor_name}</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </CardContent>
        )}
      </Card>
    </TabsContent>
  );
};
